import { FC, useMemo, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { useStyles } from './styles';
import { Paper, Tooltip, Snackbar, SnackbarContent } from '@material-ui/core';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';
import { useQuery, useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';

export const SyncDepartment: FC = observer(() => {
  const classes = useStyles();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const {
    googleStore: { syncDepartment, allsyncDepartmentsView, reSyncDepartment, reSyncSingleDepartment },
    ldapStore: { getDepartments, departmentsView },
    notificationStore: { openNotification },
  } = useStore();
  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncDepartment';
  }, []);
  const syncDepartmentReq = useQuery('syncDepartmentReq', () => syncDepartment(), {
    onError: () => openNotification('error', `We are unable to get the department from google`),
  });
  const getDepartmentsReq = useQuery('getDepartments', () => getDepartments(), {
    onError: () => openNotification('error', `Can't get count`),
  });
  const reSyncDepartmentReq = useMutation(() => reSyncDepartment(), {
    mutationKey: 'reSyncDepartment',
    onSuccess: () => {
      setOpen(false);
      setSyncedDepartments(new Set(allsyncDepartmentsView.map((department) => department.ouId)));
      openNotification('success', 'Bi-direction Sync Successful');
      syncDepartmentReq.refetch();
      getDepartmentsReq.refetch();
    },
    onError: ({ message }: Error) => {
      setOpen(false);
      openNotification('error', message);
    },
  });

  const reSyncSingleDepartmentReq = useMutation((data: { ouId: string }) => reSyncSingleDepartment(data), {
    mutationKey: 'reSyncSingleDepartmentReq',
    onSuccess: (data, variables) => {
      openNotification('success', 'Selected Departments sync’ed with App Google Successfully');
      setSyncedDepartments((prev) => new Set(prev).add(variables.ouId));
      syncDepartmentReq.refetch();
      getDepartmentsReq.refetch();
    },
    onError: (error: any) => {
      setOpen(false);
      openNotification('error', error.data.message);
    },
  });
  const handleSyncAll = () => {
    setLoadingMessage('Sync is in Progress. Please be patient, it may take few minutes.');
    reSyncDepartmentReq.mutate();
    setOpen(true);
  };

  const [syncedDepartments, setSyncedDepartments] = useState(new Set());
  const googleDepartmentCount = allsyncDepartmentsView.length ?? 0;
  const ldapDepartmentCount = departmentsView.length ?? 0;
  // const difference = Math.abs(ldapDepartmentCount - googleDepartmentCount);
  const rows = useMemo(() => {
    return !syncDepartmentReq.isFetching ? allsyncDepartmentsView : [];
  }, [syncDepartmentReq.isFetching, allsyncDepartmentsView]);

  const filteredGroupSet = useMemo(() => {
    return new Set(departmentsView.map((item) => item.orgId));
  }, [departmentsView]);

  useAppPreloader(
    syncDepartmentReq.isFetching ||
      getDepartmentsReq.isFetching ||
      reSyncDepartmentReq.isLoading ||
      reSyncSingleDepartmentReq.isLoading
  );
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <BusinessOutlinedIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'name',
      headerName: 'NAME',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.base}>
          <div>
            <span>{params.row.base}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'ouId',
      headerName: 'Google OU',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.ouId}>
          <div>
            <span>{params.row.ouId}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'description',
      headerName: 'DESCRIPTION',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.description}>
          <div>
            <span>{params.row.description}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 220,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          {!filteredGroupSet.has(params.row.ouId) && !syncedDepartments.has(params.row.ouId) ? (
            <Tooltip title='Not Synced'>
              <IconButton size='small' onClick={() => handleSync(params.row.ouId)}>
                <SyncDisabledIcon style={{ color: red[500] }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Synced'>
              <IconButton size='small' onClick={() => handleSync(params.row.ouId)}>
                <SyncIcon style={{ color: green[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const handleSync = (ouId: string) => {
    reSyncSingleDepartmentReq.mutate({ ouId });
  };

  return (
    <>
      {!syncDepartmentReq.isFetching && !getDepartmentsReq.isFetching && (
        <Paper className={classes.dataGrid} elevation={0}>
          <>
            <h1 className={classes.textBox}>You have {ldapDepartmentCount} Departments in EasyIdentity</h1>
            <h1 className={classes.textBox}>You have {googleDepartmentCount} Departments in Google Workspace</h1>
            <div style={{ display: 'flex' }}>
              <h1 className={classes.textBox}>
                Sync Departments : EasyIdentity {'<->'} Google Workspace
                <SyncIcon style={{ color: green[500], cursor: 'pointer' }} onClick={handleSyncAll} />
              </h1>
            </div>
          </>
        </Paper>
      )}
      {/* {!syncDepartmentReq.isFetching && !getDepartmentsReq.isFetching && difference === 0 && (
        <Paper className={classes.dataGrid1} elevation={0}>
          <h1 style={{ fontWeight: '400', fontSize: '120%' }}>
            Congrats.Here is the list of Departments that have been Synced with the Google Workspace.
          </h1>
        </Paper>
      )} */}
      <Paper className={classes.dataGrid} elevation={0}>
        <>
          <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>
            List of All Google Workspace Departments
          </h1>
        </>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <Snackbar open={open}>
        <SnackbarContent style={{ backgroundColor: green[500] }} message={loadingMessage} />
      </Snackbar>
    </>
  );
});
